import { DeskAttachmentItem } from "types/Desk";

/**
 * Which inline parts a message body actually draws.
 *
 * Kept in one pure module because three places need the same answer — the
 * renderer rewriting cid: sources, the panel's attachment strip, and the
 * clipboard context builder — and when the rule lived in each of them they
 * drifted apart. No React or DOM here, so the context builder stays testable.
 */

/** Normalised form of a Content-ID, with the angle brackets mail adds. */
export function cidKey(value: string): string {
  return String(value).replace(/[<>]/g, "").trim().toLowerCase();
}

/**
 * The cids the body actually draws. An inline-flagged part the HTML never
 * references (a stripped signature logo, a related part orphaned by a client
 * that rewrote the body) is drawn nowhere, so treating it as "already shown
 * above" would lose it entirely.
 */
export function referencedCids(html: string | null | undefined): Set<string> {
  const found = new Set<string>();
  if (!html) return found;
  const re = /\s(?:src|background)\s*=\s*["']cid:([^"']+)["']/gi;
  let match = re.exec(html);
  while (match) {
    const key = cidKey(match[1]);
    if (key) found.add(key);
    match = re.exec(html);
  }
  return found;
}

/**
 * Attachments worth listing: everything except the inline parts the body is
 * already drawing. A message with no bodyHtml renders as plain text, so none
 * of its attachments are drawn and all of them are listed.
 */
export function visibleAttachments(message: {
  bodyHtml: string | null;
  attachments: DeskAttachmentItem[];
}): DeskAttachmentItem[] {
  const drawn = referencedCids(message.bodyHtml);
  return message.attachments.filter(
    (a) => !(a.isInline && a.contentId && drawn.has(cidKey(a.contentId)))
  );
}
